import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Clock, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STAGES = ["requested", "acknowledged", "landed"];
const LABEL = { requested: "Requested", acknowledged: "Merchant ack", landed: "Landed in bank" };

export default function RefundTimeline({ refunds, onLoad, onLanded }) {
  const active = refunds.filter((r) => r.stage !== "landed").slice(0, 3);
  if (active.length === 0) return null;

  const advance = async (id) => {
    try {
      const { data } = await api.post(`/refunds/${id}/advance`);
      if (data.stage === "landed") {
        toast.success(`$${data.amount.toFixed(2)} from ${data.merchant} landed`);
        if (onLanded) onLanded(data);
      } else {
        toast.success("Refund stage advanced");
      }
      onLoad();
    } catch (e) {
      if (e?.response?.status !== 402) toast.error("Couldn't update refund");
    }
  };

  return (
    <Card className="tactile-card rounded-md p-5" data-testid="refund-timeline">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-bold tracking-tight">Refunds in flight</h3>
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#606872]">14-day tracker</span>
      </div>
      <div className="space-y-4">
        {active.map((r) => {
          const idx = STAGES.indexOf(r.stage);
          const created = new Date(r.created_at);
          const days = Math.floor((new Date() - created) / 86400000);
          const escalate = days >= 14 && r.stage !== "landed";
          return (
            <div key={r.id} className="border-t border-dashed border-[#282C30] pt-3 first:border-t-0 first:pt-0" data-testid={`refund-timeline-item-${r.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-bold">{r.merchant}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#606872] mt-0.5">
                    ${r.amount.toFixed(2)} · via {r.channel.replace(/_/g, " ")}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => advance(r.id)} className="btn-outline-paper h-8 text-xs" data-testid={`refund-advance-${r.id}`}>
                  Advance stage
                </Button>
              </div>
              <div className="mt-3 flex items-center gap-2">
                {STAGES.map((s, i) => (
                  <div key={s} className="flex-1 flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full grid place-items-center text-[10px] ${
                      i <= idx ? "bg-[#00E676] text-[#001b09]" : "bg-[#1C1F22] text-[#606872] border border-[#282C30]"
                    }`}>
                      {i <= idx ? <Check className="h-3 w-3" /> : i + 1}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-[#A0A7B0] hidden sm:inline whitespace-nowrap">{LABEL[s]}</div>
                    {i < 2 && <div className={`flex-1 h-px ${i < idx ? "bg-[#00E676]" : "bg-[#282C30]"}`} />}
                  </div>
                ))}
              </div>
              {escalate && (
                <div className="mt-3 flex items-center gap-2 text-[#FF3D00] text-xs font-mono uppercase tracking-widest">
                  <AlertCircle className="h-3.5 w-3.5" /> 14+ days — auto-escalate to bank chargeback
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
